import { type CSSProperties, type RefObject, type MouseEvent, useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FaUserCircle, FaClipboardList, FaBell, FaCog, FaQuestionCircle, FaCommentAlt } from 'react-icons/fa';
import { User } from '../../types/entities';
import styles from './Header.module.css';

interface DropdownMenuProps {
  currentUser?: User;
  iconUrl: string | null;
  handleAccount: () => void;
  handleLogout: () => void;
  handleAdminNotifications: () => void;
  menuRef?: RefObject<HTMLDivElement | null>;
}

const DropdownMenu = ({
  currentUser,
  iconUrl,
  handleAccount,
  handleLogout,
  handleAdminNotifications,
  menuRef,
}: DropdownMenuProps) => {
  // メニューの位置を計算
  const [position, setPosition] = useState({
    top: '70px',
    right: '25px',
  });

  useEffect(() => {
    // ユーザーアイコンの位置に基づいてドロップダウンメニューの位置を計算
    if (menuRef?.current) {
      const rect = menuRef.current.getBoundingClientRect();
      // メニューの位置を更新
      setPosition({
        top: `${rect.bottom}px`,
        right: `${window.innerWidth - rect.right}px`,
      });
    }
  }, [menuRef]);

  // 各ハンドラーをuseCallbackでラップして安定した参照を保持
  const onAccountClick = useCallback((e: MouseEvent) => {
    e.stopPropagation(); // クリックイベントの伝播のみを止める
    handleAccount();
  }, [handleAccount]);

  const onLogoutClick = useCallback((e: MouseEvent) => {
    e.stopPropagation(); // クリックイベントの伝播のみを止める
    handleLogout();
  }, [handleLogout]);

  const onAdminNotificationsClick = useCallback((e: MouseEvent) => {
    e.stopPropagation(); // クリックイベントの伝播のみを止める
    handleAdminNotifications();
  }, [handleAdminNotifications]);

  // 動的位置はCSS変数で注入し、z-indexはCSSクラスで管理
  const portalStyle = {
    '--dropdown-top': position.top,
    '--dropdown-right': position.right,
  } as CSSProperties;

  const portal = (
    <div
      style={portalStyle}
      className={`${styles.dropdownMenu} dropdown-menu-portal`}
      data-testid="header__dropdown-menu"
      onClick={(e) => {
        e.stopPropagation(); // イベントの伝播を止める
      }}
    >
      <div className={styles.dropdownHeader}>
        {iconUrl ? (
          <img
            src={iconUrl}
            alt="User Avatar"
            className={styles.dropdownAvatar}
          />
        ) : (
          <FaUserCircle className={styles.dropdownAvatar} />
        )}
        <span className={styles.dropdownUsername}>
          {currentUser?.name ?? 'User Name'}
        </span>
      </div>
      <div className={styles.dropdownDivider} />
      <button 
        className={styles.dropdownItem} 
        onClick={onAccountClick} 
        data-testid="header__account-button"
      >
        アカウント設定
      </button>
      {currentUser?.role === 'admin' && (
        <>
          <div className={styles.dropdownDivider} />
          <div className={styles.dropdownSectionTitle}>管理者メニュー</div>
          <button 
            className={`${styles.dropdownItem} ${styles.adminItem}`}
            onClick={onAdminNotificationsClick}
            data-testid="header__admin-notifications-button"
          >
            <FaBell /> 全体通知
          </button>
        </>
      )}
      <div className={styles.dropdownDivider} />
      <button className={`${styles.dropdownItem} ${styles.disabledItem}`}>
        <FaCog /> 設定 <span className={styles.comingSoon}>近日リリース</span>
      </button>
      <a 
        href="https://shimmer-rose-06e.notion.site/1c0e510b0ea5804ea3c7c4a66d7c4804?pvs=4" 
        className={styles.dropdownItem}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="header__help-link"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <FaQuestionCircle /> ヘルプ
      </a>
      <a 
        href="https://forms.gle/Y947CjXzomd3vyr66" 
        className={styles.dropdownItem}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="header__feedback-link"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <FaCommentAlt /> フィードバック
      </a>
      <div className={styles.dropdownDivider} />
      <button 
        className={styles.dropdownItem} 
        onClick={onLogoutClick}
        data-testid="header__logout-button"
      >
        ログアウト
      </button>
    </div>
  );

  // Portalを使用してbodyに直接マウント
  return ReactDOM.createPortal(portal, document.body);
};

export default DropdownMenu; 